import { EFeatures, ESubscriptionPlan } from "@/constants/enums";
import { IAggregatedProfileData, IUser } from "@/lib/database/schema/user";
import { AIProfileAnalysisResponseType } from "@/lib/integrations/ai/prompts";
import { FEATURE_ACCESS_MAP } from "@/src/constants/access";
import { ILinkedInProfile } from "@/types/dto";

export class User implements IUser {
  id: string;
  name: string;
  email: string;
  stripe_id: string;
  plan: ESubscriptionPlan | string;
  stripe_subscription_id: string;
  profile_preferences_summary?: string;
  linkedin_profile_url?: string | null;
  linkedin_username?: string | null;
  rapid_linkedin_profile_data_response?: ILinkedInProfile | null;
  ai_profile_analysis?: AIProfileAnalysisResponseType;
  ai_analyzed_at?: string;  cv_path?: string;
  cv_parsed_text?: string;
  aggregated_profile_data?: IAggregatedProfileData | null; // JSON object for aggregated profile data
  created_at?: string;
  updated_at?: string;

  constructor(userData: IUser) {
    this.id = userData.id;
    this.name = userData.name;
    this.email = userData.email;
    this.stripe_id = userData.stripe_id;
    this.plan = userData.plan;
    this.stripe_subscription_id = userData.stripe_subscription_id;
    this.profile_preferences_summary = userData.profile_preferences_summary;
    this.linkedin_profile_url = userData.linkedin_profile_url;
    this.linkedin_username = userData.linkedin_username;
    this.rapid_linkedin_profile_data_response = userData.rapid_linkedin_profile_data_response;
    this.ai_profile_analysis = userData.ai_profile_analysis;
    this.ai_analyzed_at = userData.ai_analyzed_at;
    this.cv_path = userData.cv_path;
    this.cv_parsed_text = userData.cv_parsed_text;
    this.aggregated_profile_data = userData.aggregated_profile_data;
    this.created_at = userData.created_at;
    this.updated_at = userData.updated_at;
  }

  /**
   * Check if user has a premium plan
   */
  isPremium(): boolean {
    return this.plan.toLowerCase() !== ESubscriptionPlan.NONE && this.plan.toLowerCase() !== ESubscriptionPlan.FREE;
  }

  /**
   * Check if user has LinkedIn profile data
   */
  hasLinkedInProfile(): boolean {
    return !!this.rapid_linkedin_profile_data_response;
  }

  hasLinkedInProfileURL(): boolean {
    return !!this.linkedin_profile_url;
  }

  /**
   * Check if user has CV uploaded
   */
  hasCV(): boolean {
    return !!this.cv_path && !!this.cv_parsed_text;
  }

  /**
   * Check if user has AI profile analysis
   */
  hasAIAnalysis(): boolean {
    return !!this.ai_profile_analysis;
  }

  /**
   * Get user's full name or email if name is not available
   */
  getDisplayName(): string {
    return this.name || this.email;
  }

  /**
   * Get user's subscription plan as enum
   */
  getSubscriptionPlan(): ESubscriptionPlan {
    return Object.values(ESubscriptionPlan).includes(this.plan.toLowerCase() as ESubscriptionPlan) 
      ? this.plan as ESubscriptionPlan 
      : ESubscriptionPlan.NONE;
  }

  /**
   * Check if user has completed profile setup
   */
  isProfileComplete(): boolean {
    return !!(this.profile_preferences_summary && this.linkedin_username);
  }

  hasAccess(feature:EFeatures): boolean {
    const allowedPlans = FEATURE_ACCESS_MAP[feature];
    if (allowedPlans.find(p => p.toString().toLowerCase() === this.plan.toLowerCase())) {
      return true;
    }
    return false;
  }

  noPlanSelected(): boolean {
  if (!this.plan || this.plan.toLowerCase() === ESubscriptionPlan.NONE.toString().toLowerCase()) {
    return true;
  }

  return false;
}

  /**
   * Convert back to IUser interface
   */
  toIUser(): IUser {
    return {
      id: this.id,
      name: this.name,
      email: this.email,
      stripe_id: this.stripe_id,
      plan: this.plan,
      stripe_subscription_id: this.stripe_subscription_id,
      profile_preferences_summary: this.profile_preferences_summary,
      linkedin_profile_url: this.linkedin_profile_url,
      linkedin_username: this.linkedin_username,
      rapid_linkedin_profile_data_response: this.rapid_linkedin_profile_data_response,
      ai_profile_analysis: this.ai_profile_analysis,
      ai_analyzed_at: this.ai_analyzed_at,
      cv_path: this.cv_path,
      cv_parsed_text: this.cv_parsed_text,  
      aggregated_profile_data: this.aggregated_profile_data,    
      created_at: this.created_at,
      updated_at: this.updated_at,
    };
  }
}
